import { addDays, differenceInMinutes, startOfDay } from "date-fns";

export const MINUTES_PER_DAY = 24 * 60;

export interface TimedItem {
  startMinute: number;
  endMinute: number;
}

export interface PositionedItem<T> {
  item: T;
  /** Zero-based column inside its overlap cluster. */
  column: number;
  /** Number of columns in its overlap cluster. */
  columns: number;
}

/**
 * Returns the minutes covered by an event inside `day`, clipped to the day
 * boundaries, or null when the event does not intersect the day.
 */
export function clipToDay(
  startAt: string,
  endAt: string,
  day: Date,
): TimedItem | null {
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (end <= dayStart || start >= dayEnd) {
    return null;
  }
  return {
    startMinute: Math.max(0, differenceInMinutes(start, dayStart)),
    endMinute: Math.min(MINUTES_PER_DAY, differenceInMinutes(end, dayStart)),
  };
}

/**
 * Lays out the events of a day like a calendar app: events that overlap share
 * the width of the column, each one in the first free sub-column.
 */
export function layoutDayEvents<T extends TimedItem>(
  items: T[],
): PositionedItem<T>[] {
  const sorted = [...items].sort(
    (a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute,
  );

  const positioned: PositionedItem<T>[] = [];
  let cluster: Array<{ item: T; column: number }> = [];
  let columnEnds: number[] = [];
  let clusterEnd = -1;

  const flushCluster = () => {
    const columns = columnEnds.length;
    cluster.forEach(({ item, column }) =>
      positioned.push({ item, column, columns }),
    );
    cluster = [];
    columnEnds = [];
  };

  for (const item of sorted) {
    if (item.startMinute >= clusterEnd) {
      flushCluster();
    }
    const freeColumn = columnEnds.findIndex((end) => end <= item.startMinute);
    const column = freeColumn === -1 ? columnEnds.length : freeColumn;
    columnEnds = [
      ...columnEnds.slice(0, column),
      item.endMinute,
      ...columnEnds.slice(column + 1),
    ];
    cluster = [...cluster, { item, column }];
    clusterEnd = Math.max(clusterEnd, item.endMinute);
  }
  flushCluster();

  return positioned;
}
